import { Component } from '@theme/component';

const constants = {
  TIMEOUT_MS: 10_000,
};

/** Error whose message is already customer facing, so it can be rendered as is. */
class GiftCardError extends Error {
  /** @type {unknown} */
  cause;

  /** @param {string} message @param {unknown} [cause] */
  constructor(message, cause) {
    super(message);
    this.cause = cause;
  }
}

/**
 * @typedef {Object} Refs
 * @property {HTMLInputElement} codeInput
 * @property {HTMLInputElement} [pinInput]
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} error
 * @property {HTMLElement} result
 * @property {HTMLElement} balance
 * @extends {Component<Refs>}
 */
export class GiftCardBalance extends Component {
  requiredRefs = ['codeInput', 'button', 'error', 'result', 'balance'];

  /** @type {AbortController | null} */
  #abort = null;

  #lastCheckedKey = '';

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  get #pinPrefixes() {
    return (this.dataset.pinPrefixes || '')
      .split(',')
      .map((prefix) => prefix.trim())
      .filter(Boolean);
  }

  updatePinVisibility() {
    const { codeInput, pinInput } = this.refs;

    if (!pinInput) return;

    const needsPin = this.#pinPrefixes.some((prefix) => codeInput.value.trim().startsWith(prefix));

    pinInput.hidden = !(pinInput.required = needsPin);
  }

  /** @param {SubmitEvent} event */
  async checkBalance(event) {
    event.preventDefault();

    const { codeInput, pinInput, button, error, result, balance } = this.refs;

    const code = codeInput.value.trim();
    const pin = pinInput?.value.trim() || null;
    const key = `${code}|${pin ?? ''}`;

    if (!code || key === this.#lastCheckedKey) return;

    error.hidden = true;
    result.hidden = true;
    button.disabled = true;

    try {
      const data = await this.#fetchBalance(code, pin);

      this.#lastCheckedKey = key;
      balance.textContent = `${data.currency} ${data.currentBalance.toFixed(2)}`;
      result.hidden = false;
    } catch (err) {
      error.textContent =
        (err instanceof Error && err.message) || Theme.translations.gift_card_server_error;
      error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  /**
   * Resolves the message for a failed response: the message set for the status code when there is
   * one, then the message returned by the API, then the generic one.
   * @param {Response} response
   */
  async #errorMessage(response) {
    const { gift_card_server_error: fallback, gift_card_not_found_error: notFound } =
      Theme.translations;

    if (response.status === 404) return notFound || fallback;

    // Server errors can carry internal details, so only 4XX bodies are shown to the customer.
    if (response.status >= 500) return fallback;

    const message = await response
      .json()
      .then((json) => json.message || '')
      .catch(() => '');

    return message || fallback;
  }

  /** @param {string} giftcard @param {string | null} pin */
  async #fetchBalance(giftcard, pin) {
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), constants.TIMEOUT_MS);

    const signal = AbortSignal.any
      ? AbortSignal.any([this.#abort.signal, timeout.signal])
      : timeout.signal;

    const body = {
      action: 'balance',
      giftvoucherReference: giftcard,
      market: Shopify.country,
      ...(pin && { pin }),
    };

    try {
      const response = await fetch('/apps/giftcard/enquire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) throw new GiftCardError(await this.#errorMessage(response));

      const { data } = await response.json();

      if (typeof data?.currentBalance !== 'number') {
        const { gift_card_server_error: fallback, gift_card_invalid_balance_error: invalidBalance } =
          Theme.translations;

        throw new GiftCardError(invalidBalance || fallback);
      }

      return data;
    } catch (err) {
      if (err instanceof GiftCardError) throw err;

      // Network failures, aborts and unreadable payloads all read as the service being down.
      throw new GiftCardError(Theme.translations.gift_card_server_error, err);
    } finally {
      clearTimeout(timer);
    }
  }
}

if (!customElements.get('gift-card-balance')) {
  customElements.define('gift-card-balance', GiftCardBalance);
}
